import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Cart, createMarketplaceCartItem, createPortalProductCartItem } from '../models/cart.model';
import { computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { removeAllEntities, removeEntity, setEntity, withEntities } from '@ngrx/signals/entities';
import { PortalProduct } from '../../portal-products/models/portal-product.model';
import { MemberQuery } from '../../../account/member/member.query';
import { MarketplaceItemV2 } from '../../marketplace/marketplace-v2/models/marketplace-item-v2.model';
import { isEqual } from '../../../shared/utility-functions';
import { MarketplaceBasketCustomFieldModel } from '../../marketplace/models/basket/marketplace-basket-customfield.model';
import { calculateTotalPrice, instanceOfMarketplaceItem, instanceOfPortalProduct } from './cart.helper';
import { MarketplaceV2Query } from '../../marketplace/marketplace-v2/marketplace-v2.query';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { MarketplaceV2Service } from '../../marketplace/marketplace-v2/marketplace-v2.service';
import { MarketplaceItemAttributeValue } from '../../marketplace/models/attributes/marketplace-item-attribute-value.model';

interface CartState {
  refreshTrigger: number;
}

const initialState: CartState = {
  refreshTrigger: 0,
};

export interface CartUpdaterFn {
  (state: Cart | undefined): Partial<Cart>;
}

type CartItem = MarketplaceItemV2 | PortalProduct;

/**
 * Store for cart items in version 2 of the add to cart module. This store uses the new signal-based store from ngrx signals.
 */
export const CartV2Store = signalStore(
  { providedIn: 'root' },
  withState<CartState>(initialState),
  withEntities<Cart>(),
  withComputed(({ entities, refreshTrigger }, marketplaceService = inject(MarketplaceV2Service)) => {
    const items$ = combineLatest([toObservable(entities), toObservable(refreshTrigger)]).pipe(
      switchMap(([cartEntities]) => {
        if (cartEntities.length === 0) return of([] as Cart[]);
        return combineLatest(
          cartEntities.map((entity) => {
            if (instanceOfMarketplaceItem(entity.productData)) {
              return marketplaceService.fetchMarketplaceItemById((entity.productData as MarketplaceItemV2).Id).pipe(
                map((latestData) => {
                  const updatedEntity: Cart = { ...entity, productData: { ...entity.productData, ...latestData } };
                  return { ...updatedEntity, totalPrice: calculateTotalPrice(updatedEntity) };
                }),
                catchError(() => of({ ...entity, totalPrice: calculateTotalPrice(entity) } as Cart))
              );
            }
            return of({ ...entity, totalPrice: calculateTotalPrice(entity) } as Cart);
          })
        );
      })
    );
    return {
      items: toSignal(items$, { initialValue: [] as Cart[] }),
    };
  }),
  withMethods((store, memberQuery = inject(MemberQuery), marketplaceQuery = inject(MarketplaceV2Query)) => {
    return {
      updateById(id: string, itemOrUpdater: Partial<Cart> | CartUpdaterFn) {
        const item = this.getEntity(id);
        if (!item()) {
          return;
        }
        const patch = typeof itemOrUpdater === 'function' ? itemOrUpdater(item()) : itemOrUpdater;
        patchState(store, setEntity({ ...item(), ...patch } as Cart));
      },
      getEntity(id: string) {
        return computed(() => store.entityMap()[id]);
      },
      getCartItem(itemId: number, attributeMappingId?: number, customFields?: MarketplaceBasketCustomFieldModel[]) {
        return computed(() => {
          return store
            .entities()
            .find(
              (entity) =>
                ((entity.productData as MarketplaceItemV2).Id == itemId ||
                  (entity.productData as PortalProduct).ProductId == itemId) &&
                (attributeMappingId === undefined || entity.attributes.AttributeMappingId == attributeMappingId) &&
                (customFields === undefined || (customFields ? isEqual(entity.customFields, customFields) : !entity.customFields))
            );
        });
      },
      upsertCartItem(
        item: CartItem,
        quantity: number,
        selectedAttribute?: MarketplaceItemAttributeValue,
        customFields?: MarketplaceBasketCustomFieldModel[]
      ) {
        const memberId = memberQuery.getUserContext().memberId;
        if (instanceOfMarketplaceItem(item)) {
          const cartItem = this.getCartItem(item.Id, selectedAttribute?.AttributeMappingId, customFields);
          const cartId: any = cartItem()?.id;
          if (cartItem()) {
            this.updateById(cartId, { purchaseQuantity: cartItem()!.purchaseQuantity + quantity });
          } else {
            this.addCartItem(createMarketplaceCartItem(item, quantity, memberId, selectedAttribute, customFields) as Cart);
          }
        } else if (instanceOfPortalProduct(item)) {
          this.addCartItem(createPortalProductCartItem(item, 1, memberId) as Cart);
        }
      },
      addCartItem(cartItem: Cart) {
        patchState(store, setEntity(cartItem));
      },
      remove(id: string) {
        patchState(store, removeEntity(id));
      },
      removeAll() {
        patchState(store, removeAllEntities());
      },
      refreshItems() {
        patchState(store, { refreshTrigger: store.refreshTrigger() + 1 });
      },
    };
  })
);
